/**
 * Utility functions for table calculations
 */

/**
 * Calculate the sum of values in a column
 * @param {Array} data - Table data array
 * @param {number} columnIndex - Index of the column to calculate
 * @returns {number} Sum of all numeric values in the column
 */
export const calculateSum = (data, columnIndex) => {
  if (!data || !Array.isArray(data)) return 0;

  return data.reduce((sum, row) => {
    const value = row[`col_${columnIndex}`];
    const numericValue = parseFloat(value);
    return isNaN(numericValue) ? sum : sum + numericValue;
  }, 0);
};

/**
 * Calculate the average of values in a column
 * @param {Array} data - Table data array
 * @param {number} columnIndex - Index of the column to calculate
 * @returns {number} Average of all numeric values in the column
 */
export const calculateAverage = (data, columnIndex) => {
  if (!data || !Array.isArray(data)) return 0;

  const validValues = data.filter((row) => {
    const value = row[`col_${columnIndex}`];
    return !isNaN(parseFloat(value)) && value !== "" && value != null;
  });

  if (validValues.length === 0) return 0;

  const sum = calculateSum(data, columnIndex);
  return sum / validValues.length;
};

/**
 * Calculate the count of non-empty values in a column
 * @param {Array} data - Table data array
 * @param {number} columnIndex - Index of the column to calculate
 * @returns {number} Count of non-empty values in the column
 */
export const calculateCount = (data, columnIndex) => {
  if (!data || !Array.isArray(data)) return 0;

  return data.filter((row) => {
    const value = row[`col_${columnIndex}`];
    return value !== "" && value != null;
  }).length;
};

/**
 * Get the calculation function based on operation type
 * @param {string} operation - The operation type (addition, average, count)
 * @returns {Function} The calculation function
 */
export const getCalculationFunction = (operation) => {
  switch (operation) {
    case "addition":
      return calculateSum;
    case "average":
      return calculateAverage;
    case "count":
      return calculateCount;
    default:
      return calculateSum;
  }
};

/**
 * Get the display name for an operation
 * @param {string} operation - The operation type
 * @returns {string} The display name
 */
export const getOperationDisplayName = (operation) => {
  switch (operation) {
    case "addition":
      return "Total";
    case "average":
      return "Average";
    case "count":
      return "Count";
    default:
      return "Addition";
  }
};
